import {
  achievementClaimResponseSchema,
  adClaimResponseSchema,
  adStatusResponseSchema,
  API_PREFIX,
  apiErrorSchema,
  authResponseSchema,
  deleteMeResponseSchema,
  equipResponseSchema,
  hintPurchaseResponseSchema,
  leaderboardMeResponseSchema,
  leaderboardResponseSchema,
  playerSnapshotSchema,
  profileResponseSchema,
  purchaseResponseSchema,
  recoveryIssueResponseSchema,
  statusResponseSchema,
  syncResponseSchema,
  type ErrorCode,
  type EquipRequest,
  type GuestCreateRequest,
  type HintPurchaseRequest,
  type ProfilePatchRequest,
  type PurchaseRequest,
  type RecoveryRestoreRequest,
  type SyncRequest,
} from "@nano/contracts";
import type { z } from "zod";

export type ClientErrorCode = ErrorCode | "NETWORK" | "TIMEOUT";

export class ApiClientError extends Error {
  constructor(
    readonly code: ClientErrorCode,
    readonly status: number,
    readonly retryAfterSec?: number,
  ) {
    super(code);
  }

  /** Worth retrying later with backoff (offline, quota, server hiccup). */
  get transient(): boolean {
    return (
      this.code === "NETWORK" ||
      this.code === "TIMEOUT" ||
      this.code === "RATE_LIMITED" ||
      this.code === "SERVICE_TEMPORARILY_UNAVAILABLE" ||
      this.code === "SYNC_CONFLICT" ||
      this.code === "INTERNAL" ||
      this.status >= 500
    );
  }
}

const TIMEOUT_MS = 10_000;

export class ApiClient {
  constructor(
    private readonly baseUrl: string,
    private readonly getToken: () => string | null,
    private readonly fetchImpl: typeof fetch = (...args) => fetch(...args),
  ) {}

  private async request<T extends z.ZodTypeAny>(
    method: string,
    path: string,
    schema: T,
    body?: unknown,
    auth = true,
  ): Promise<z.infer<T>> {
    const headers: Record<string, string> = {};
    const token = auth ? this.getToken() : null;
    if (token) headers.authorization = `Bearer ${token}`;
    if (body !== undefined) headers["content-type"] = "application/json";
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${API_PREFIX}${path}`, {
        method,
        headers,
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        signal: controller.signal,
        credentials: "omit",
      });
    } catch (error) {
      throw new ApiClientError((error as Error).name === "AbortError" ? "TIMEOUT" : "NETWORK", 0);
    } finally {
      clearTimeout(timer);
    }
    let json: unknown = null;
    try {
      json = await response.json();
    } catch {
      json = null;
    }
    if (!response.ok) {
      const parsed = apiErrorSchema.safeParse(json);
      if (parsed.success) {
        throw new ApiClientError(parsed.data.error.code, response.status, parsed.data.error.retryAfterSec);
      }
      throw new ApiClientError(response.status === 429 ? "RATE_LIMITED" : response.status >= 500 ? "INTERNAL" : "NETWORK", response.status);
    }
    const parsed = schema.safeParse(json);
    if (!parsed.success) throw new ApiClientError("INTERNAL", response.status);
    return parsed.data as z.infer<T>;
  }

  status() {
    return this.request("GET", "/status", statusResponseSchema, undefined, false);
  }
  createGuest(body: GuestCreateRequest) {
    return this.request("POST", "/guest", authResponseSchema, body, false);
  }
  me() {
    return this.request("GET", "/me", playerSnapshotSchema);
  }
  sync(body: SyncRequest) {
    return this.request("POST", "/progress/sync", syncResponseSchema, body);
  }
  purchase(body: PurchaseRequest) {
    return this.request("POST", "/shop/purchase", purchaseResponseSchema, body);
  }
  buyHint(body: HintPurchaseRequest) {
    return this.request("POST", "/hints/purchase", hintPurchaseResponseSchema, body);
  }
  equip(body: EquipRequest) {
    return this.request("PUT", "/avatar/equipment", equipResponseSchema, body);
  }
  patchProfile(body: ProfilePatchRequest) {
    return this.request("PATCH", "/profile", profileResponseSchema, body);
  }
  leaderboard(limit: number) {
    return this.request("GET", `/leaderboard?limit=${limit}`, leaderboardResponseSchema, undefined, false);
  }
  leaderboardMe() {
    return this.request("GET", "/leaderboard/me", leaderboardMeResponseSchema);
  }
  issueRecovery() {
    return this.request("POST", "/recovery/issue", recoveryIssueResponseSchema, {});
  }
  adStatus() {
    return this.request("GET", "/ads/status", adStatusResponseSchema);
  }
  claimAd(reward: string) {
    return this.request("POST", "/ads/claim", adClaimResponseSchema, { reward });
  }
  deleteMe() {
    return this.request("DELETE", "/me", deleteMeResponseSchema);
  }
  claimAchievement(achievementId: string) {
    return this.request("POST", "/achievements/claim", achievementClaimResponseSchema, { achievementId });
  }
  restore(body: RecoveryRestoreRequest) {
    return this.request("POST", "/recovery/restore", authResponseSchema, body, false);
  }
}
